import { errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { FieldsEditor } from "./-components/fields-editor";
import { PdfPanel } from "./-components/pdf-panel";
import { SignerSlotsPanel } from "./-components/signer-slots-panel";
import { NONE } from "./-components/template-create-form";
import {
  TemplateSettingsForm,
  templateSettingsToInput,
} from "./-components/template-settings-form";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/document-templates/$templateId",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "document-templates"),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.administration.documentTemplates.get.queryOptions({
        input: { templateId: params.templateId },
      }),
    ),
  component: DocumentTemplateEditorPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One template: what it is called, the PDF, who signs it, and the boxes. A published template is
 * frozen — editing it would change what people have already agreed to — so everything here is
 * read-only until it is forked into the next draft version. */
function DocumentTemplateEditorPage() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan("settings:manage");

  const { data: template } = useSuspenseQuery(
    orpc.administration.documentTemplates.get.queryOptions({ input: { templateId } }),
  );
  const categories = useQuery(
    orpc.administration.documentTemplates.categories.list.queryOptions({
      input: { includeInactive: false },
    }),
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.documentTemplates.key() });

  const update = useMutation(
    orpc.administration.documentTemplates.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Template updated");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const publish = useMutation(
    orpc.administration.documentTemplates.publish.mutationOptions({
      onSuccess: async () => {
        toast.success("Template published. It can be sent now.");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const fork = useMutation(
    orpc.administration.documentTemplates.fork.mutationOptions({
      onSuccess: async (created) => {
        toast.success(`Editing draft v${created.version}. The published version is unchanged.`);
        await invalidate();
        await navigate({
          to: "/settings/document-templates/$templateId",
          params: { templateId: created.id },
        });
      },
      onError: fail,
    }),
  );

  const duplicate = useMutation(
    orpc.administration.documentTemplates.duplicate.mutationOptions({
      onSuccess: async (created) => {
        toast.success(`“${created.name}” carries every signer and field box across.`);
        await invalidate();
        await navigate({
          to: "/settings/document-templates/$templateId",
          params: { templateId: created.id },
        });
      },
      onError: fail,
    }),
  );

  const isDraft = template.status === "draft";
  const readOnly = !canWrite || !isDraft;

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {template.name}
            <Badge
              variant={template.status === "active" ? "ok" : "secondary"}
              className="capitalize"
            >
              {template.status}
            </Badge>
            <Badge variant="outline">v{template.version}</Badge>
            {template.isLatestVersion ? null : <Badge variant="outline">Superseded</Badge>}
          </span>
        }
        meta={
          <Link to="/settings/document-templates" className="hover:underline">
            Document templates
          </Link>
        }
        actions={
          canWrite ? (
            <span className="flex flex-wrap gap-2">
              {/* A separate form that starts here, rather than this one's next version. */}
              <Button
                variant="outline"
                disabled={duplicate.isPending}
                onClick={() => duplicate.mutate({ templateId })}
              >
                {duplicate.isPending ? "Duplicating…" : "Duplicate template"}
              </Button>
              {isDraft ? (
                <Button
                  disabled={publish.isPending || Boolean(template.blockedFromPublishing)}
                  onClick={() => publish.mutate({ templateId })}
                >
                  {publish.isPending ? "Publishing…" : "Publish template"}
                </Button>
              ) : template.status === "active" && template.isLatestVersion ? (
                <Button disabled={fork.isPending} onClick={() => fork.mutate({ templateId })}>
                  {fork.isPending ? "Creating…" : "Create new version"}
                </Button>
              ) : null}
            </span>
          ) : null
        }
      />

      {readOnly && canWrite ? (
        <Alert>
          <AlertTitle>This version is frozen</AlertTitle>
          <AlertDescription>
            Changing a published template would change what people have already signed. Create a new
            version to make edits. The documents already signed keep printing exactly as they were.
          </AlertDescription>
        </Alert>
      ) : null}

      {isDraft && template.blockedFromPublishing ? (
        <Alert>
          <AlertTitle>Not ready to publish</AlertTitle>
          <AlertDescription>{template.blockedFromPublishing}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <TemplateSettingsForm
              key={templateId}
              defaultValues={{
                templateId,
                name: template.name,
                description: template.description ?? "",
                categoryId: template.categoryId ?? NONE,
                signerOrderEnforced: template.signerOrderEnforced,
                allowPartialSigning: template.allowPartialSigning,
                expirationDays: template.expirationDays,
              }}
              categories={categories.data ?? []}
              disabled={readOnly}
              pending={update.isPending}
              onSubmit={(values) => update.mutate(templateSettingsToInput(values))}
            />
          </CardContent>
        </Card>

        <div className="space-y-6">
          <PdfPanel
            templateId={templateId}
            storageKey={template.originalPdfStorageKey}
            pageCount={template.pdfPageCount}
            readOnly={readOnly}
          />
          <SignerSlotsPanel templateId={templateId} slots={template.slots} canWrite={!readOnly} />
        </div>
      </div>

      {/* Saving replaces every field row, so a new set of ids remounts the
          editor on what was actually stored. */}
      <FieldsEditor
        key={`${templateId}:${template.fields.map((field) => field.id).join(",")}`}
        templateId={templateId}
        slots={template.slots}
        savedFields={template.fields}
        readOnly={readOnly}
      />
    </div>
  );
}
